import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, Layers, BookOpen, UserCog, X } from "lucide-react";
import { api } from "../../lib/api";

const GROUPS = [["students", "Siswa", Users], ["groups", "Rombel", Layers], ["modules", "Modul", BookOpen], ["teachers", "Pengajar", UserCog]];

export function GlobalSearch() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); } if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => { if (open) setTimeout(() => ref.current?.focus(), 50); else { setQ(""); setRes(null); } }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q.trim())}`).then((r) => { setRes(r.data); setIdx(0); }).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [q]);
  const flat = res ? GROUPS.flatMap(([k]) => (res[k] || []).map((x) => ({ ...x, kind: k }))) : [];
  const go = (item) => { setOpen(false); nav(item.to); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, flat.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && flat[idx]) go(flat[idx]);
  };
  return (
    <>
      <button type="button" data-testid="global-search-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 h-9 rounded-full border border-slate-200 bg-white px-3 text-xs text-slate-500 hover:border-sky-400 hover:text-[color:var(--brand-blue)] transition-colors dark:bg-slate-800 dark:border-slate-700">
        <Search className="h-3.5 w-3.5" /><span className="hidden md:inline">Cari siswa, rombel, modul…</span><kbd className="hidden md:inline rounded border border-slate-200 bg-slate-50 px-1 text-[10px] font-mono dark:bg-slate-700 dark:border-slate-600">Ctrl K</kbd>
      </button>
      {open && <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] px-4" data-testid="global-search-dialog">
        <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
        <div className="relative w-full max-w-xl surface shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-200 px-4 dark:border-slate-700">
            <Search className="h-4 w-4 text-slate-400" />
            <input ref={ref} data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder="Ketik nama siswa, NIK, rombel, modul, pengajar…" className="h-12 flex-1 bg-transparent text-sm outline-none" />
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
          </div>
          <div className="max-h-[55vh] overflow-y-auto p-2" data-testid="global-search-results">
            {q.trim().length < 2 && <p className="px-3 py-6 text-center text-xs text-slate-400">Ketik minimal 2 huruf. Navigasi dengan ↑ ↓, buka dengan Enter.</p>}
            {res && flat.length === 0 && <p className="px-3 py-6 text-center text-xs text-slate-400" data-testid="global-search-empty">Tidak ada hasil untuk “{q}”.</p>}
            {GROUPS.map(([k, label, Icon]) => (res?.[k] || []).length > 0 && <div key={k} className="mb-1">
              <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
              {res[k].map((x) => { const i = flat.findIndex((f) => f.kind === k && f.id === x.id); return (
                <button key={x.id} type="button" data-testid={`global-search-item-${k}`} onMouseEnter={() => setIdx(i)} onClick={() => go(x)} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left ${i === idx ? "bg-sky-50 dark:bg-slate-700" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-slate-100 text-[color:var(--brand-blue)] dark:bg-slate-700"><Icon className="h-4 w-4" /></span>
                  <span className="min-w-0"><span className="block truncate text-sm font-medium">{x.title}</span><span className="block truncate text-xs text-slate-500">{x.sub}</span></span>
                </button>); })}
            </div>)}
          </div>
        </div>
      </div>}
    </>
  );
}
